import type { CustomField, CustomFieldValueSet } from '@/lib/api/types';
import { Input, Select } from '@/components/Input';
import { cx } from '@/lib/format';
import { valueFor } from './policy';

/** A controlled editor for one custom field value (form use: board defaults, the upload "Complete metadata" step). */
export function FieldValueInput({ field, value, onChange, id, invalid }: { field: CustomField; value: CustomFieldValueSet | null; onChange: (v: CustomFieldValueSet | null) => void; id?: string; invalid?: boolean }) {
  const cls = cx('h-7 text-xs', invalid && 'border-warn');
  switch (field.type) {
    case 'select':
      return (
        <Select id={id} aria-label={field.name} className={cls} value={value?.value_option_ids?.[0] ?? ''} onChange={(e) => onChange(valueFor(field, e.target.value))}>
          <option value="">—</option>
          {field.options?.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </Select>
      );
    case 'multi_select': {
      const on = new Set(value?.value_option_ids ?? []);
      return (
        <div id={id} role="group" aria-label={field.name} className="flex flex-wrap gap-1">
          {field.options?.map((o) => (
            <button
              key={o.id}
              type="button"
              aria-pressed={on.has(o.id)}
              onClick={() => {
                const next = new Set(on);
                if (next.has(o.id)) next.delete(o.id);
                else next.add(o.id);
                onChange(valueFor(field, [...next]));
              }}
              className={cx('rounded-full border px-2 py-0.5 text-xs', on.has(o.id) ? 'border-accent bg-accent-soft text-accent' : 'border-line')}
            >
              {o.label}
            </button>
          ))}
        </div>
      );
    }
    case 'number':
      return <Input id={id} aria-label={field.name} type="number" className={cls} value={value?.value_number != null ? String(value.value_number) : ''} onChange={(e) => onChange(valueFor(field, e.target.value))} />;
    case 'date':
      return <Input id={id} aria-label={field.name} type="date" className={cls} value={value?.value_date?.slice(0, 10) ?? ''} onChange={(e) => onChange(valueFor(field, e.target.value))} />;
    default:
      return <Input id={id} aria-label={field.name} className={cls} value={value?.value_text ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : { value_text: e.target.value })} />;
  }
}
